import { ReactCommentParams } from '@/infra/api/comments/types/ReactComment';
import { ReactCommentResult } from '@/infra/api/comments/types/shared';
import { Interceptor } from '@/infra/api/core/interceptors';
import { ResponseResult } from '@/infra/api/core/request';
import { RemoveCommentReactParams } from '@/infra/api/comments/types/RemoveCommentReact';
import {
  CommentsReactedVariables,
  ReactionInfo,
} from '@/infra/api/comments/types/CommentsReactedQuery';
import { type ReplyCommentMutationVariables } from '@/infra/api/comments/types/CommentsQuery';
import { type Comment } from '@/domain/entities/Comment';
import { getProductComments } from '../../endpoints';

export const reactProductCommentInterceptor: Interceptor<
  ResponseResult<ReactCommentResult, ReactCommentParams>,
  void
> = ({ incoming: { data, variables } }) => {
  if (!data) return;

  const productComments = getProductComments.cache$.value;

  const id = variables?.id || '';
  const reacted = variables?.reaction;
  const { reactions } = data;

  if (!productComments?.length) return;

  const updatedComments = productComments.map((comment) => {
    if (comment.id === id) {
      const { totalReactionsCount } = comment;

      const nextTotalReactionsCount = totalReactionsCount + 1;

      return {
        ...comment,
        reactions,
        reacted,
        totalReactionsCount: nextTotalReactionsCount,
      };
    }
    return comment;
  });

  getProductComments.cache$.next(updatedComments);
};

export const removeReactionProductCommentInterceptor: Interceptor<
  ResponseResult<ReactCommentResult, RemoveCommentReactParams>,
  void
> = ({ incoming: { data, variables } }) => {
  if (!data) return;

  const productComments = getProductComments.cache$.value;

  const id = variables?.id || '';
  const { reactions } = data;

  if (!productComments?.length) return;

  const updatedComments = productComments.map((comment) => {
    if (comment.id === id) {
      const { totalReactionsCount } = comment;

      const nextTotalReactionsCount = totalReactionsCount - 1;

      return {
        ...comment,
        reactions,
        reacted: undefined,
        totalReactionsCount: nextTotalReactionsCount,
      };
    }
    return comment;
  });

  getProductComments.cache$.next(updatedComments);
};

export const getCommentsReactedProductInterceptor: Interceptor<
  ResponseResult<ReactionInfo[], CommentsReactedVariables>,
  void
> = ({ incoming: { data } }) => {
  if (!data) return;

  const productComments = getProductComments.cache$.value;

  if (!productComments?.length) return;

  const mappedComments = productComments.map((comment) => {
    const reactionInfo = data.find(
      (reaction) => reaction.commentId === comment.id
    );

    return reactionInfo
      ? { ...comment, reacted: reactionInfo.reacted }
      : comment;
  });

  getProductComments.cache$.next(mappedComments);
};

export const replyProductCommentInterceptor: Interceptor<
  ResponseResult<Comment, ReplyCommentMutationVariables>,
  void
> = ({ incoming: { data, variables } }) => {
  if (!data) return;

  const topProductCommentsCache = getProductComments.cache$.value;
  const isProductComment = variables?.isProductComment || false;

  if (!isProductComment || !topProductCommentsCache) return;

  const updatedTopComments = [data, ...topProductCommentsCache];

  getProductComments.cache$.next(updatedTopComments);
};
